import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, of, take } from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { ArtPillComponent } from '../../components/art-pill/art-pill.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { ResPillComponent } from '../../components/res-pill/res-pill.component';
import { RunSummaryCardComponent } from '../../components/run-summary-card/run-summary-card.component';
import { TestRunHandoverModalComponent } from '../../components/test-run-handover-modal/test-run-handover-modal.component';
import { TestRunAbortDialogComponent } from '../../components/test-run-abort-dialog/test-run-abort-dialog.component';
import { ABORTABLE_STATUSES, abortErrorKeyFor } from '../../components/test-run-abort-dialog/test-run-abort';
import {
  CommentStepReference,
  TestRunCommentsComponent,
} from '../../components/test-run-comments/test-run-comments.component';
import { PersonOption } from '../../components/person-select/person-select.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { AccountService } from '../../account/account.service';
import {
  ResultStatus,
  RunnerState,
  RunnerStep,
  TestRun,
  TestRunHandoverRequest,
  TestRunPauseState,
  TestRunSource,
} from '../../models/test-run.model';
import { formatDurationMs } from '../../shared/duration.util';
import { reviewerOptionsFromMembers } from '../../shared/project-member-options';
import { ProjectMemberService } from '../../services/project-member.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { TestRunService } from '../../services/test-run.service';
import { TestRunLiveEvent, TestlaufLiveFeedService } from '../../services/testlauf-live-feed.service';
import { TestlaufRunnerService } from '../../services/testlauf-runner.service';
import { runnerPillStatus, runnerResultKey } from './runner-status';
import { decidedSteps, leafSteps } from './runner-steps';
import { numberSteps } from './step-numbering';
import { TestlaufRunnerLiveComponent } from './testlauf-runner-live.component';
import { TestlaufRunnerManuellComponent } from './testlauf-runner-manuell.component';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';
import { findRouteParam } from '../../shared/route-params';

const SNACK_DURATION_MS = 5000;

/**
 * Nur die Aktionen, die an einer Regel ueber dem Lauf haengen — pausieren,
 * uebergeben, uebernehmen —, nennen diesen Schluessel bei einem 403. Der Abschluss
 * haengt an der Projekt-Berechtigung; dort erklaerte ein Satz ueber Ausfuehrende und
 * Empfaenger die falsche Regel.
 */
const FORBIDDEN_KEY = 'testRunner.pause.forbidden';

/**
 * Welcher Satz zu welcher Absage gehoert. Ein Konflikt meint immer denselben
 * veralteten Stand, unabhaengig davon, welche Aktion ihn ausgeloest hat.
 */
function messageKeyFor(status: number, errorKey: string, forbiddenKey?: string): string {
  if (status === 409) return 'testRunner.staleError';
  if (status === 403 && forbiddenKey) return forbiddenKey;
  return errorKey;
}

/**
 * Der Runner eines Durchlaufs (NEX-1042). Diese Seite haelt Kopfdaten,
 * Fortschritt und den Zustand; die Durchfuehrung selbst liegt in der Variante zur
 * Art des Laufs — manuell im Master-Detail-Runner, automatisiert in der
 * Live-Pipeline-Ansicht.
 */
@Component({
  selector: 'app-testlauf-runner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    Modal,
    ArtPillComponent,
    CodeIdChipComponent,
    PageHeaderComponent,
    ResPillComponent,
    RunSummaryCardComponent,
    ProjectNavComponent,
    AvatarComponent,
    TestRunHandoverModalComponent,
    TestRunAbortDialogComponent,
    TestRunCommentsComponent,
    TestlaufRunnerLiveComponent,
    TestlaufRunnerManuellComponent,
  ],
  providers: [DatePipe, TestlaufLiveFeedService],
  templateUrl: './testlauf-runner.component.html',
  styleUrl: './testlauf-runner.component.scss',
})
export class TestlaufRunnerComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly datePipe = inject(DatePipe);
  private readonly router = inject(Router);
  private readonly runnerService = inject(TestlaufRunnerService);
  private readonly liveFeed = inject(TestlaufLiveFeedService);
  private readonly testRunService = inject(TestRunService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly accountService = inject(AccountService);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = this.findParam('projectId');
  readonly runId = this.findParam('runId');
  /**
   * Nicht {@code readonly}: „Erneut ausloesen" wechselt auf einen anderen Durchlauf
   * desselben Laufs (NEX-1236). Das laeuft ueber denselben Routen-Knoten, Angular haelt
   * die Komponente und tauscht nur den Parameter — der Startwert aus dem Snapshot bliebe
   * sonst stehen und die Seite zeigte weiter den vorigen Durchlauf.
   */
  executionId = this.findParam('executionId');

  readonly state = signal<RunnerState | null>(null);
  readonly loading = signal(true);

  /**
   * Ein Neuladen bei laufender Arbeit — die Variante bleibt dabei stehen. Wuerde
   * stattdessen {@code loading} greifen, baute der Spinner-Zweig sie ab, und mit
   * ihr stuerben die Entwuerfe, die gerade jemand getippt hat.
   */
  readonly reloading = signal(false);
  readonly loadError = signal(false);

  readonly completing = signal(false);

  /** Zaehlt hoch, wenn die Variante zum fehlgeschlagenen Schritt ohne Ist fuehren soll. */
  readonly revealMissingActual = signal(0);

  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  /** Was die Variante ueber ihre ungespeicherten Eingaben meldet. */
  private readonly unsavedInput = signal(false);
  readonly hasUnsavedInput = this.unsavedInput.asReadonly();

  readonly automated = computed(() => this.state()?.runKind === 'AUTOMATED');

  readonly startedAtLabel = computed(() => {
    const startedAt = this.state()?.startedAt;
    return startedAt ? this.datePipe.transform(startedAt, 'dd.MM.yyyy HH:mm') : null;
  });

  /**
   * Ob der Durchlauf zu Ende ist. `live` ist genau dann falsch, wenn sein Status nicht
   * mehr `RUNNING` ist — die Entitaet fuehrt dafuer bewusst kein eigenes Feld. Der
   * Runner weicht dann der Abschluss-Karte.
   */
  readonly finished = computed(() => {
    const runnerState = this.state();
    return runnerState !== null && !runnerState.live;
  });

  /**
   * Ob es ueberhaupt etwas abzuschliessen gibt. Vor dem ersten Laden, bei einem
   * automatisierten Lauf und bei einem bereits abgeschlossenen Durchlauf nicht — der
   * Knopf dazu steht im Fuss des Schritt-Details der manuellen Variante, wie im
   * Entwurf (page-testlauf.jsx:849), und die rendert in genau diesen Faellen nicht.
   */
  readonly completable = computed(() => this.state() !== null && !this.automated() && !this.finished());

  /**
   * Die Quelle des Laufs steht nicht im Runner-Zustand. Die Live-Ansicht braucht sie fuer den
   * CI-Report (NEX-1672), der Absprung zu den Ergebnissen fuer Runner-Laeufe (NEX-1597).
   */
  protected readonly runSource = signal<TestRunSource | null>(null);

  /**
   * Ein abgeschlossener Durchlauf eines verbundenen Rechners (TCDL) hat Schritt-Ergebnisse,
   * die die Live-Ansicht nicht zeigt (NEX-1597). Pipeline-Laeufe haben keine.
   */
  readonly showResultsOffered = computed(() => this.automated() && this.finished() && this.runSource() === 'TCDL');

  /** Gezaehlt wird ueber {@link leafSteps} — Gruppenknoten tragen kein eigenes Ergebnis. */
  readonly summaryFailed = computed(() => this.countSteps('FAILED'));
  readonly summaryBlocked = computed(() => this.countSteps('BLOCKED'));
  readonly summarySkipped = computed(() => this.countSteps('SKIPPED'));

  /** Ohne Dauer steht der Strich; {@link formatDurationMs} liefert ihn selbst. */
  readonly summaryDuration = computed(() => formatDurationMs(this.state()?.durationMs));

  /** Der Status des fertigen Durchlaufs — Symbol, Farbe und Pille der Karte haengen daran. */
  readonly summaryStatus = computed<ResultStatus>(() => this.state()?.status ?? 'NOT_RUN');

  readonly aborted = computed(() => this.state()?.runStatus === 'ABORTED');

  /**
   * Die Begruendung eines abgebrochenen Laufs. {@code null}: es gibt keine;
   * {@code undefined}: sie ist (noch) nicht bekannt — die Karte nennt dann keine.
   */
  readonly abortReason = signal<string | null | undefined>(undefined);

  readonly abortOpen = signal(false);
  readonly abortSubmitting = signal(false);
  readonly abortErrorKey = signal<string | null>(null);
  /** Nach dem Abbruch verschwindet dessen Knopf — der Fokus geht auf die Abschluss-Karte. */
  readonly focusSummary = signal(false);
  /** Ein 409 hat den Stand als veraltet gemeldet; geladen wird erst nach dem Schliessen des Dialogs. */
  private reloadAfterAbortDialog = false;

  /**
   * Ob „Lauf abbrechen" in der Kopfleiste steht (NEX-1315). Anders als Pausieren und
   * Uebergeben haengt es nicht an Ausfuehrenden oder Zugewiesenen, sondern an der
   * Schreibberechtigung im Projekt — auch ein uebergebener Lauf und einer ohne Schritte
   * laesst sich abbrechen. Den automatisierten Lauf beendet die Aktion der Pipeline.
   */
  readonly abortOffered = computed(() => {
    const runnerState = this.state();
    if (!runnerState || this.automated() || this.finished() || !runnerState.callerCanWrite) return false;
    return ABORTABLE_STATUSES.includes(runnerState.runStatus);
  });

  private countSteps(status: ResultStatus): number {
    const runnerState = this.state();
    if (!runnerState) return 0;
    return leafSteps(runnerState.cases).filter((step) => step.actualStatus === status).length;
  }

  readonly subtitle = computed(() => {
    const runnerState = this.state();
    if (!runnerState) return '';
    const name = runnerState.runName ?? '';
    return runnerState.environment
      ? this.translate.instant('testRunner.subtitleWithEnvironment', {
          name,
          environment: runnerState.environment,
        })
      : name;
  });

  /** „1/4 bestanden" wie im Design (`page-testlauf.jsx:287`). */
  readonly passedOfTotal = computed(() => {
    const runnerState = this.state();
    return runnerState ? `${runnerState.stepsPassed}/${runnerState.stepsTotal}` : '';
  });

  /** Der Anteil bewerteter Schritte (NEX-1496) — bestanden sagt schon {@link passedOfTotal}. */
  readonly progress = computed(() => {
    const runnerState = this.state();
    if (!runnerState || runnerState.stepsTotal === 0) return 0;
    return Math.round((decidedSteps(runnerState.cases).length / runnerState.stepsTotal) * 100);
  });

  /** Projektmitglieder als moegliche Empfaenger — ohne die aufrufende Person. */
  readonly handoverPeople = computed<PersonOption[]>(() => {
    const me = this.currentUserId();
    return this.projectPeople().filter((person) => person.id !== me);
  });

  private readonly projectPeople = signal<PersonOption[]>([]);

  /** Wer angemeldet ist; das Kommentar-Panel entscheidet daran ueber die Autor-Aktionen. */
  readonly currentUserId = signal<string | null>(null);

  readonly handoverOpen = signal(false);
  readonly handoverSubmitting = signal(false);
  /** Ein Lebenszyklus-Aufruf laeuft; solange bleiben Pausieren/Fortsetzen gesperrt. */
  readonly lifecycleBusy = signal(false);

  readonly pauseState = computed<TestRunPauseState | null>(() => this.state()?.pauseState ?? null);

  /**
   * Ob Pausieren und Uebergeben ueberhaupt zur Sache gehoeren: nur ein manueller
   * Lauf wird durchgeklickt, und nur solange sein Durchlauf noch laeuft und der
   * Lauf selbst nicht abgeschlossen oder abgebrochen ist. Dieselbe Bedingung, die
   * das Backend prueft — die Oberflaeche bietet nichts an, was dort 409 gaebe.
   */
  readonly lifecycleAvailable = computed(() => {
    const runnerState = this.state();
    if (!runnerState || this.automated() || !runnerState.live) return false;
    return runnerState.runStatus === 'RUNNING' || runnerState.runStatus === 'PAUSED';
  });

  readonly paused = computed(() => this.lifecycleAvailable() && this.state()?.runStatus === 'PAUSED');

  /**
   * Ob diese Pause aus einer Uebergabe stammt — und nicht bloss daraus, dass der Lauf
   * ueberhaupt jemandem gehoert.
   *
   * <p>`assignedToUserId` allein beantwortet das nicht: `TestRunCreator` setzt das Feld
   * schon beim Anlegen aus `owner`, und `takeOver` setzt es erneut, waehrend `resumed()`
   * nur Notiz und Abteilung abraeumt. Wer seinen eigenen Lauf pausiert, traegt das Feld
   * also laengst, ohne dass je jemand etwas uebergeben haette.
   *
   * <p>Drei Merkmale sind eindeutig: eine Abteilung und eine Notiz setzt ausschliesslich
   * `handOver`, und ein Lauf, der gerade jemand anderem gehoert, liegt aus Sicht der
   * lesenden Person bei einem Empfaenger. Bleibt der Fall „an mich uebergeben, ohne
   * Notiz" — dort zeigt die Seite das blaue Banner. Das ist kein Verlust: es gibt keine
   * Notiz anzuzeigen, und das Backend laesst die zugewiesene Person ohnehin
   * fortsetzen ({@code requireResumeAllowed}), was genau das ist, was der Knopf tut.
   */
  readonly handedOver = computed(() => {
    const pauseState = this.pauseState();
    if (!this.paused() || !pauseState) return false;
    if (pauseState.assignedToGroup !== null || pauseState.handoverNote !== null) return true;
    return pauseState.assignedToUserId !== null && pauseState.assignedToUserId !== this.currentUserId();
  });

  readonly pausedOnly = computed(() => this.paused() && !this.handedOver());

  /**
   * Ob die Kommentar-Historie ein Formular anbietet.
   *
   * <p>Ein automatisierter Lauf wird nicht kommentiert, ein abgeschlossener oder
   * abgebrochener nimmt nichts mehr an ({@code TestRunMutationGuard}), und eine nur
   * lesende Rolle bekaeme vom Backend ein 403. Gelesen wird die Historie in allen
   * drei Faellen weiter.
   */
  readonly canComment = computed(() => {
    const runnerState = this.state();
    if (!runnerState || this.automated() || this.projectRoleService.isProjectReader()) return false;
    return runnerState.runStatus !== 'COMPLETED' && runnerState.runStatus !== 'ABORTED';
  });

  /**
   * Wohin die Kommentare dieses Durchlaufs zeigen: {@code stepResultId} → Kennung,
   * Titel, Schrittnummer und Ergebnis (NEX-1201).
   *
   * <p>Einmal je Zustand aufgeloest statt je Kommentar gesucht — die Historie kennt
   * nur die Kennung des Schrittergebnisses, und ein Lauf bringt hunderte Schritte mit.
   *
   * <p>Kennung und Titel stammen vom Reihenfolge-Element, nicht vom Fall-Ergebnis:
   * die linke Spalte des Runners nennt genau diese beiden, und ein umbenannter Fall
   * traegt im Ergebnis noch den Namen vom Start des Laufs. Findet sich kein Element
   * — etwa bei einem Lauf aus einem Plan, dessen Elemente auf den Plan zeigen —,
   * uebernimmt der Fall selbst.
   *
   * <p>Gruppenknoten bleiben aussen vor: sie tragen kein Ergebnis, an ihnen haengt
   * kein Kommentar.
   */
  readonly commentStepReferences = computed<Record<string, CommentStepReference>>(() => {
    const runnerState = this.state();
    if (runnerState === null) return {};
    const elements = new Map(runnerState.order.map((element) => [element.refId, element]));
    const references: Record<string, CommentStepReference> = {};
    for (const runnerCase of runnerState.cases) {
      const element = elements.get(runnerCase.testCaseId);
      const elementKey = element?.key ?? runnerCase.key;
      const elementTitle = element?.title ?? runnerCase.title;
      for (const entry of numberSteps(runnerCase.steps)) {
        if (entry.isGroup) continue;
        references[entry.step.id] = {
          elementKey,
          elementTitle,
          stepNumber: entry.number,
          status: runnerPillStatus(entry.step.actualStatus),
          statusLabelKey: runnerResultKey(entry.step.actualStatus),
        };
      }
    }
    return references;
  });

  /**
   * Die Leitungs-Frage beantwortet der Server (NEX-1180). Sie haengt dort an
   * {@code Permission.PROJECT_ADMIN} und schliesst damit Mandanten- und
   * Plattform-Administratoren ein, die im Projekt gar nicht Mitglied sind — der
   * Rollen-Code einer Mitgliedschaft kennt die nicht und haette still abweichende
   * Knoepfe gezeigt.
   */
  private readonly isLead = computed(() => this.state()?.callerIsLead === true);

  /** Ob die aufrufende Person den Lauf ausfuehrt — `isExecutor` im Backend. */
  private readonly isExecutor = computed(() => {
    const me = this.currentUserId();
    return me !== null && this.state()?.executedByUserId === me;
  });

  /**
   * Ob der Lauf der aufrufenden Person zugewiesen ist — `isAssignee` im Backend.
   *
   * <p>Der Zugewiesene steht im Pause-Zustand und nicht in `assigneeUserId`: das Feld
   * traegt den Bearbeiter des <em>Durchlaufs</em>, die Uebergabe haengt dagegen am
   * <em>Lauf</em> — und danach fragt das Backend.
   */
  private readonly isAssignee = computed(() => {
    const me = this.currentUserId();
    return me !== null && this.pauseState()?.assignedToUserId === me;
  });

  /** Ob der Lauf ueberhaupt jemandem gehoert — `hasAssignment` im Backend. */
  private readonly hasAssignment = computed(() => {
    const pause = this.pauseState();
    return pause !== null && (pause.assignedToUserId !== null || pause.assignedToGroup !== null);
  });

  /**
   * Ob Pausieren und Uebergeben in der Kopfleiste ueberhaupt erscheinen: erlaubt sind
   * Ausfuehrende, Zugewiesene und die Projektleitung, wie in
   * {@code TestRunLifecycleService#requirePauseOrHandoverAllowed}. Ein Knopf, den das
   * Backend mit 403 abweist, steht gar nicht erst da — deaktiviert waere er ein
   * Versprechen, das der naechste Klick bricht (NEX-1180).
   */
  readonly pauseOrHandoverOffered = computed(
    () => this.lifecycleAvailable() && (this.isExecutor() || this.isAssignee() || this.isLead()),
  );

  /**
   * Ob der Fortsetzen-Knopf im Pause-Banner erscheint. Fortsetzen folgt einer eigenen
   * Regel ({@code TestRunLifecycleService#requireResumeAllowed}): ist der Lauf jemandem
   * zugewiesen, gehoert es dieser Person und der Projektleitung, sonst der/dem
   * Ausfuehrenden. Der Ersteller zoege sonst ueber `/resume` genau den Lauf wieder an
   * sich, den `/take-over` ihm verwehrt.
   */
  readonly resumeOffered = computed(() => {
    if (!this.paused()) return false;
    if (this.isLead()) return true;
    return this.hasAssignment() ? this.isAssignee() : this.isExecutor();
  });

  readonly canPause = computed(() => this.pauseOrHandoverOffered() && !this.paused() && !this.lifecycleBusy());
  readonly canHandOver = computed(() => this.pauseOrHandoverOffered() && !this.lifecycleBusy());
  readonly canResume = computed(() => this.resumeOffered() && !this.lifecycleBusy());

  /**
   * Uebernehmen darf der benannte Empfaenger und die Projektleitung — und jede und
   * jeder, wenn der Lauf an eine Abteilung ging, denn die benennt niemanden
   * persoenlich. Dieselbe Regel entscheidet im Backend; hier steht sie, damit
   * niemand einen Knopf sieht, der ihm nur ein 403 einbringt.
   */
  readonly canTakeOver = computed(() => {
    const pauseState = this.pauseState();
    if (!this.handedOver() || !pauseState || this.lifecycleBusy()) return false;
    if (pauseState.assignedToUserId === null) return true;
    return this.isAssignee() || this.isLead();
  });

  readonly recipientName = computed(() => {
    const pauseState = this.pauseState();
    return pauseState?.assignedToName ?? pauseState?.assignedToGroup ?? '';
  });

  readonly recipientKindKey = computed(() =>
    this.pauseState()?.assignedToGroup ? 'testRunner.handover.recipientGroup' : 'testRunner.handover.recipientPerson',
  );

  readonly handoverNote = computed(() => this.pauseState()?.handoverNote ?? '');

  /**
   * Wo der Zwischenstand haengt, als „X/Y" ueber die auswertbaren Schritte. Leer,
   * solange noch keiner ausgewertet wurde — dann gibt es keinen Schritt zu nennen,
   * und eine „0/8" waere eine Zahl ueber nichts.
   */
  readonly pausedAtStep = computed(() => {
    const runnerState = this.state();
    const stepResultId = this.pauseState()?.pausedAtStepResultId;
    if (!runnerState || !stepResultId) return '';
    const leaves = leafSteps(runnerState.cases);
    const position = leaves.findIndex((step) => step.id === stepResultId);
    return position < 0 ? '' : `${position + 1}/${leaves.length}`;
  });

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedInput());
    this.liveFeed.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => this.applyLiveEvent(event));
    this.destroyRef.onDestroy(() => this.liveFeed.disconnect());
    this.load();
    this.loadHandoverCandidates();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const executionId = params.get('executionId');
      if (!executionId || executionId === this.executionId) {
        return;
      }
      this.executionId = executionId;
      // Auf null und damit in den Lade-Zweig: der neue Durchlauf hat mit dem bisherigen
      // Stand nichts zu tun, und ein stehengebliebener zeigte fremde Zahlen.
      this.state.set(null);
      this.load();
    });
  }

  /**
   * Empfaenger und eigene Kennung stehen schon vor dem ersten Klick bereit: der
   * Uebergabe-Dialog oeffnet sonst mit einer leeren Personenliste, und das
   * Uebernehmen-Banner wuesste nicht, ob es sich an die aufrufende Person richtet.
   */
  private loadHandoverCandidates(): void {
    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => this.currentUserId.set(profile.id),
        error: () => this.currentUserId.set(null),
      });
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (members) => this.projectPeople.set(reviewerOptionsFromMembers(members)),
        error: () => this.projectPeople.set([]),
      });
  }

  private load(onLoaded?: () => void): void {
    const first = this.state() === null;
    if (first) {
      this.loading.set(true);
      this.loadError.set(false);
    } else {
      this.reloading.set(true);
    }
    this.runnerService
      .getState(this.projectId, this.runId, this.executionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runnerState) => {
          this.state.set(runnerState);
          this.loadAbortReason(runnerState);
          this.loadRunSource(runnerState);
          // Ein abgeschlossener Durchlauf nimmt die Variante mit, und mit ihr die
          // Entwuerfe in ihren Feldern. Ohne dieses Zuruecksetzen fragte der Guard
          // beim Weggehen nach etwas, das gar nicht mehr da steht.
          if (!runnerState.live) this.unsavedInput.set(false);
          this.watchLiveFeed(runnerState);
          this.loading.set(false);
          this.reloading.set(false);
          onLoaded?.();
        },
        error: () => {
          this.loading.set(false);
          this.reloading.set(false);
          // Ein gescheitertes Neuladen laesst den vorhandenen Stand stehen: die
          // Fehlerseite an seiner Stelle wuerde die Arbeit wegwerfen, um zu
          // melden, dass der Server gerade nicht antwortet.
          if (first) this.loadError.set(true);
        },
      });
  }

  /**
   * Fuer jeden automatisierten Durchlauf, laufend, abgeschlossen oder abgebrochen, und nur
   * einmal. Scheitert das Laden, bleibt die Quelle unbekannt: kein CI-Report, kein Absprung.
   */
  private loadRunSource(runnerState: RunnerState): void {
    if (runnerState.runKind !== 'AUTOMATED' || this.runSource() !== null) {
      return;
    }
    this.testRunService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => this.runSource.set(run.source),
        error: () => {
          // Bewusst still: ohne Quelle fehlt nur der CI-Report.
        },
      });
  }

  /**
   * Die Begruendung steht nicht im Runner-Zustand, nur am Lauf — geholt wird sie darum
   * nur fuer einen abgebrochenen. Scheitert das Laden, bleibt stehen, was bekannt ist:
   * die Karte zeigt „Abgebrochen" ohne Begruendung, statt den Lauf als Fehler zu melden.
   * Ein automatisierter Lauf zeigt keine Abschluss-Karte, dort laese sie niemand.
   */
  private loadAbortReason(runnerState: RunnerState): void {
    if (runnerState.runStatus !== 'ABORTED' || runnerState.runKind === 'AUTOMATED') {
      this.abortReason.set(undefined);
      return;
    }
    this.testRunService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => this.abortReason.set(run.abortReason ?? null),
        error: () => {
          // Bewusst still: der Lauf bleibt als abgebrochen erkennbar.
        },
      });
  }

  /**
   * Die manuelle Strecke hoert auf dem Topic ihres Durchlaufs mit (NEX-1244). Ohne
   * das erfuehre sie einen fremden Abschluss erst daran, dass der naechste Schritt
   * mit einem 409 abgewiesen wird — also erst, nachdem jemand vergeblich gearbeitet
   * hat. Der automatisierte Lauf bleibt aussen vor: dort haelt die Live-Variante
   * bereits ein eigenes Abonnement auf demselben Topic.
   *
   * <p>{@link TestlaufLiveFeedService#connect} baut nur die erste Verbindung auf,
   * darum darf ein Neuladen hier folgenlos wieder hereinlaufen.
   */
  private watchLiveFeed(runnerState: RunnerState): void {
    if (runnerState.runKind === 'AUTOMATED') {
      return;
    }
    this.liveFeed.connect(this.projectId, this.runId, this.executionId);
  }

  /**
   * Der Abschluss des eigenen Durchlaufs friert die Seite an Ort und Stelle ein.
   * Ergebnis, Dauer und Schrittzahlen kommen aus dem Ereignis: die Karte steht damit
   * sofort, ohne auf eine Antwort des Servers zu warten.
   *
   * <p>Das Nachladen danach holt, was das Ereignis nicht traegt — fehlgeschlagene und
   * uebersprungene Schritte zaehlt die Karte aus den Schritten selbst, und die koennen
   * von der Person stammen, die den Durchlauf gerade abgeschlossen hat. Erst
   * einfrieren, dann nachziehen: umgekehrt stuende die Seite bis zur Antwort weiter
   * offen zur Eingabe.
   *
   * <p>Von `step-updated` zaehlt nur der Status einer Modulzeile (NEX-1325). Die
   * uebrigen Ereignisse gehen den manuellen Runner nichts an — er fuehrt seine
   * Schritte selbst fort — und ein fremder Durchlauf schon gar nicht.
   */
  private applyLiveEvent(event: TestRunLiveEvent): void {
    const runnerState = this.state();
    if (event.type === 'step-updated' && runnerState !== null) {
      this.applyModuleStatus(runnerState, event);
      return;
    }
    if (event.type !== 'pipeline-completed' || runnerState === null) return;
    if (event.executionId !== runnerState.executionId || !runnerState.live) return;
    // Der eigene Abschluss kommt ueber die Antwort seines Aufrufs zurueck. Das
    // Ereignis geht nach dem Commit raus und kann sie ueberholen — ohne diese
    // Unterscheidung meldete die Seite der Person, die gerade selbst abgeschlossen
    // hat, jemand anderes sei ihr zuvorgekommen.
    if (this.completing()) return;

    this.state.set({
      ...runnerState,
      live: false,
      status: event.status ?? runnerState.status,
      finishedAt: event.at ?? runnerState.finishedAt,
      durationMs: event.durationMs ?? runnerState.durationMs,
      stepsPassed: event.stepsPassed ?? runnerState.stepsPassed,
      stepsTotal: event.stepsTotal ?? runnerState.stepsTotal,
    });
    // Die Variante geht mitsamt ihren Entwuerfen; ohne dieses Zuruecksetzen fragte
    // der Guard beim Weggehen nach Eingaben, die es nicht mehr gibt.
    this.unsavedInput.set(false);
    this.snackBar.open(this.translate.instant('testRunner.completedElsewhere'), undefined, {
      duration: SNACK_DURATION_MS,
    });
    this.load();
  }

  /**
   * Den Status eines Moduls leitet der Server aus seinen Kindern ab (NEX-1323) und meldet
   * ihn als `step-updated` (NEX-1325). Uebernommen wird nur die Modulzeile:
   * Blattschritte schreibt die Antwort des eigenen Aufrufs, und ein fremdes Ereignis
   * wuerde dort offene Eingaben ueberholen.
   */
  private applyModuleStatus(runnerState: RunnerState, event: TestRunLiveEvent): void {
    if (event.executionId !== runnerState.executionId || event.status === undefined) return;
    const status = event.status;
    const isModuleRow = (step: RunnerStep) => step.id === event.stepResultId && step.kind === 'MODULE';
    if (!runnerState.cases.some((runnerCase) => runnerCase.steps.some(isModuleRow))) return;
    this.state.set({
      ...runnerState,
      cases: runnerState.cases.map((runnerCase) => ({
        ...runnerCase,
        steps: runnerCase.steps.map((step) => (isModuleRow(step) ? { ...step, actualStatus: status } : step)),
      })),
    });
  }

  readonly pillStatus = runnerPillStatus;
  readonly resultKey = runnerResultKey;

  /** Zurueck zur Liste der Durchlaeufe — der Guard fragt bei offenen Eingaben nach. */
  goToExecutions(): void {
    void this.router.navigate(['/projects', this.projectId, 'test-runs', this.runId, 'executions']);
  }

  goToResults(): void {
    void this.router.navigate([
      '/projects',
      this.projectId,
      'test-runs',
      this.runId,
      'executions',
      this.executionId,
      'results',
    ]);
  }

  /**
   * Zurueck zur Testlauf-Uebersicht, wie der Entwurf den Knopf der Kopfleiste fuehrt
   * (page-testlauf.jsx:287). Die Zwischenebene der Durchlaeufe haengt im Breadcrumb.
   */
  goToOverview(): void {
    void this.router.navigate(['/projects', this.projectId, 'test-runs']);
  }

  /**
   * Abschliessen gehoert der Seite, nicht der Variante: der Durchlauf ist ihr
   * Gegenstand, und der Knopf dazu steht in ihrer Kopfleiste. Die Variante meldet
   * nur, ob ihre Schritte so weit sind.
   *
   * <p>Die offenen Entwuerfe gelten danach als erledigt — sonst fragt der Guard
   * beim Weiternavigieren nach ihnen, und wer dann bleibt, sitzt auf einem
   * abgeschlossenen Durchlauf fest, der jeden Speicherversuch mit 409 abweist.
   */
  complete(): void {
    const runnerState = this.state();
    if (!runnerState || !this.completable() || this.completing()) return;
    this.completing.set(true);
    this.runnerService
      .complete(this.projectId, this.runId, runnerState.executionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (execution) => {
          this.completing.set(false);
          // Die Variante ist nach dem Abschluss fort, ihre Entwuerfe mit ihr — ohne
          // dieses Zuruecksetzen fragte der Guard nach etwas, das es nicht mehr gibt.
          this.unsavedInput.set(false);
          // Die Antwort traegt Status, Dauer und Zaehler des fertigen Durchlaufs; ein
          // Neuladen brauchte es nur, um dieselben Werte noch einmal zu holen.
          this.state.update((current) =>
            current === null
              ? current
              : {
                  ...current,
                  status: execution.status,
                  live: execution.live,
                  durationMs: execution.durationMs,
                  stepsPassed: execution.stepsPassed,
                  stepsTotal: execution.stepsTotal,
                },
          );
        },
        error: (error: HttpErrorResponse) => {
          this.completing.set(false);
          if (error.status !== 400) {
            this.reportRunnerError(error, 'testRunner.completeError');
            return;
          }
          // Der einzige 400-Grund des Abschlusses ist requireActualResultForFailedSteps
          // in TestRunRunnerService.complete. Den betroffenen Schritt nennt er nicht —
          // der frische Stand zeigt ihn.
          this.reportRunnerError(error, 'testRunner.completeErrorMissingActual');
          this.load(() => this.revealMissingActual.update((token) => token + 1));
        },
      });
  }

  pause(): void {
    if (!this.canPause()) return;
    this.runLifecycle(this.testRunService.pause(this.projectId, this.runId), 'testRunner.pause.error');
  }

  resume(): void {
    if (!this.canResume()) return;
    this.runLifecycle(this.testRunService.resume(this.projectId, this.runId), 'testRunner.pause.resumeError');
  }

  takeOver(): void {
    if (!this.canTakeOver()) return;
    this.runLifecycle(this.testRunService.takeOver(this.projectId, this.runId), 'testRunner.handover.takeOverError');
  }

  openHandover(): void {
    if (!this.canHandOver()) return;
    this.handoverOpen.set(true);
  }

  closeHandover(): void {
    if (this.handoverSubmitting()) return;
    this.handoverOpen.set(false);
  }

  /**
   * Der Dialog bleibt bis zur Antwort stehen und schliesst erst, wenn die Uebergabe
   * wirklich gespeichert ist. Schloesse er sofort, verschwaende die getippte Notiz
   * mit ihm, sobald der Server ablehnt.
   */
  submitHandover(request: TestRunHandoverRequest): void {
    if (this.handoverSubmitting()) return;
    this.handoverSubmitting.set(true);
    this.testRunService
      .handover(this.projectId, this.runId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pauseState) => {
          this.handoverSubmitting.set(false);
          this.handoverOpen.set(false);
          this.applyPauseState(pauseState);
        },
        error: (error: HttpErrorResponse) => {
          this.handoverSubmitting.set(false);
          this.reportRunnerError(error, 'testRunner.handover.error', FORBIDDEN_KEY);
        },
      });
  }

  openAbort(): void {
    if (!this.abortOffered()) return;
    this.abortErrorKey.set(null);
    this.reloadAfterAbortDialog = false;
    this.abortOpen.set(true);
  }

  /** Nach einem 409 zeigt der Dialog erst die Absage; den aktuellen Stand holt das Schliessen. */
  closeAbort(): void {
    if (this.abortSubmitting()) return;
    this.abortOpen.set(false);
    if (this.reloadAfterAbortDialog) {
      this.reloadAfterAbortDialog = false;
      this.load();
    }
  }

  /**
   * Wie bei der Uebergabe bleibt der Dialog bis zur Antwort stehen; eine Absage
   * erscheint in ihm, die Begruendung bleibt stehen.
   *
   * <p>Nach dem Erfolg friert die Seite sofort ein — Status und Begruendung stehen in
   * der Antwort. Das Nachladen danach holt, was sie nicht traegt: Dauer und Zaehler
   * des beim Abbruch geschlossenen Durchlaufs.
   */
  submitAbort(reason: string): void {
    if (this.abortSubmitting() || !this.abortOpen()) return;
    this.abortSubmitting.set(true);
    this.abortErrorKey.set(null);
    this.testRunService
      .abort(this.projectId, this.runId, reason)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          this.abortSubmitting.set(false);
          this.abortOpen.set(false);
          this.focusSummary.set(true);
          this.applyAbortedRun(run);
          this.load();
        },
        error: (error: HttpErrorResponse) => {
          this.abortSubmitting.set(false);
          this.abortErrorKey.set(abortErrorKeyFor(error.status));
          if (error.status === 409) this.reloadAfterAbortDialog = true;
        },
      });
  }

  private applyAbortedRun(run: TestRun): void {
    // Die Variante geht mitsamt ihren Entwuerfen; ohne dieses Zuruecksetzen fragte der
    // Guard beim Weggehen nach Eingaben, die es nicht mehr gibt.
    this.unsavedInput.set(false);
    this.abortReason.set(run.abortReason ?? null);
    this.state.update((current) =>
      current === null
        ? current
        : {
            ...current,
            runStatus: run.status,
            live: false,
            status: run.outcome ?? current.status,
            pauseState: { ...current.pauseState, status: run.status },
          },
    );
  }

  private runLifecycle(call: Observable<TestRunPauseState>, errorKey: string): void {
    this.lifecycleBusy.set(true);
    call.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (pauseState) => {
        this.lifecycleBusy.set(false);
        this.applyPauseState(pauseState);
      },
      error: (error: HttpErrorResponse) => {
        this.lifecycleBusy.set(false);
        this.reportRunnerError(error, errorKey, FORBIDDEN_KEY);
      },
    });
  }

  /**
   * Die Antwort traegt denselben Zustand, den auch ein Neuladen liefert — sie wird
   * in den vorhandenen geschrieben statt nachgeladen. Ein Neuladen an dieser Stelle
   * baute die Variante neu auf und nähme die Entwuerfe mit, die gerade jemand tippt.
   */
  private applyPauseState(pauseState: TestRunPauseState): void {
    const runnerState = this.state();
    if (!runnerState) return;
    this.state.set({ ...runnerState, runStatus: pauseState.status, pauseState });
  }

  /**
   * Meldet einen gescheiterten Schreibzugriff auf den Durchlauf und holt bei einem
   * Konflikt den aktuellen Stand: ein 409 heisst, der Lauf steht laengst woanders —
   * jemand anderes hat ihn inzwischen fortgesetzt oder abgeschlossen. Eine Meldung
   * ueber einen Zustand, den es nicht mehr gibt, waere dort nutzlos.
   */
  private reportRunnerError(error: HttpErrorResponse, errorKey: string, forbiddenKey?: string): void {
    const stale = error.status === 409;
    this.snackBar.open(this.translate.instant(messageKeyFor(error.status, errorKey, forbiddenKey)), undefined, {
      duration: SNACK_DURATION_MS,
    });
    if (stale) this.load();
  }

  onStaleState(): void {
    this.load();
  }

  /** Die Variante hat den Zustand fortgeschrieben — Kopf und Fortschritt folgen. */
  onStateChanged(runnerState: RunnerState): void {
    this.state.set(runnerState);
  }

  onUnsavedInputChange(unsaved: boolean): void {
    this.unsavedInput.set(unsaved);
  }

  /**
   * Ungespeicherte Eingaben liegen in der Variante, gebraucht werden sie hier, weil
   * der Router-Guard an dieser Seite haengt. Die Variante meldet sie; die Seite
   * greift nicht in deren Klasse. Sonst faellt der Guard fuer jede weitere Variante
   * stillschweigend aus, weil ein {@code viewChild} auf die manuelle Klasse dort
   * leer bleibt.
   */
  confirmDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedInput()) return of(true);
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  /** Die Projekt-ID steht an einer Elternroute, Lauf und Durchlauf an dieser. */
  private findParam(name: string): string {
    return findRouteParam(this.route, name) ?? '';
  }
}
